// Lia's mouth, on the client side: fetches a Segment's WAV from the server (cached per
// line, so a replay re-bills nothing), plays it through WebAudio so the recorder can tap
// it and an AnalyserNode can drive the mouth. Ported from office-news voice.ts, plus the
// analyser. The AudioContext must be born inside a click, or it starts suspended and the
// show plays silent — every entry point in the studio calls voiceStream() on a click.
import { fetchVoiceBlob } from "../../../services/influioTvService";

const cache = new Map(); // `${voice}|${text}` → object URL
const MAX_CACHE = 60;
const key = (voice, text) => `${voice}|${text}`;

export const hasVoice = (voice, text) => cache.has(key(voice, text));

export async function fetchVoice(voice, text) {
    const k = key(voice, text);
    const hit = cache.get(k);
    if (hit) return hit;
    const url = URL.createObjectURL(await fetchVoiceBlob(text, voice));
    if (cache.size >= MAX_CACHE) {
        const oldest = cache.keys().next().value;
        URL.revokeObjectURL(cache.get(oldest));
        cache.delete(oldest);
    }
    cache.set(k, url);
    return url;
}

/** Length of a cached line in seconds — the studio budgets the tape's bitrate with it. */
export const voiceSeconds = (url) => new Promise((resolve) => {
    const a = new Audio(url);
    a.onloadedmetadata = () => resolve(a.duration || 0);
    a.onerror = () => resolve(0);
});

let audioCtx = null;
let capture = null;
let analyser = null;
let samples = null;
let current = null;

/** The cast voice as a MediaStream for the recorder. Call from a user gesture. */
export function voiceStream() {
    audioCtx ??= new (window.AudioContext || window.webkitAudioContext)();
    void audioCtx.resume();
    capture ??= audioCtx.createMediaStreamDestination();
    if (!analyser) {
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.5;
        samples = new Uint8Array(analyser.fftSize);
    }
    return capture.stream;
}

/** 0..1 loudness of the line playing right now; 0 when silent. */
export function mouthLevel() {
    if (!analyser || !current) return 0;
    analyser.getByteTimeDomainData(samples);
    let sum = 0;
    for (let i = 0; i < samples.length; i++) { const v = (samples[i] - 128) / 128; sum += v * v; }
    const rms = Math.sqrt(sum / samples.length);
    return Math.min(1, rms * 5);
}

/** Resolves when the line ends (or fails to start: the show must not park). */
export function playVoice(url) {
    return new Promise((resolve) => {
        stopVoice();
        const a = new Audio(url);
        current = a;
        if (audioCtx?.state === "running" && capture) {
            const src = audioCtx.createMediaElementSource(a);
            src.connect(analyser);
            analyser.connect(audioCtx.destination);
            src.connect(capture);
        }
        const done = () => { if (current === a) { current = null; } resolve(); };
        a.onended = done;
        a.onerror = done;
        a.play().catch(done);
    });
}

export function stopVoice() {
    if (current) { current.onended = null; current.pause(); current = null; }
}
